import type { OHLCVData, Timeframe } from '../types'

// Binance API base URL (public, no auth needed)
const BINANCE_API = 'https://api.binance.com/api/v3'

// Convert our timeframe to Binance interval
const timeframeToInterval: Record<Timeframe, string> = {
  '1m': '1m',
  '5m': '5m',
  '15m': '15m',
  '30m': '30m',
  '1h': '1h',
  '4h': '4h',
  '1d': '1d',
  '1w': '1w',
  '1M': '1M',
}

// Convert our symbol to Binance format
function toBinanceSymbol(symbol: string): string {
  // BTC/USDT -> BTCUSDT
  return symbol.replace('/', '')
}

// Fetch klines (candlestick) data from Binance
export async function fetchBinanceKlines(
  symbol: string, 
  timeframe: Timeframe, 
  limit: number = 500
): Promise<OHLCVData[]> {
  const binanceSymbol = toBinanceSymbol(symbol)
  const interval = timeframeToInterval[timeframe]
  
  const url = `${BINANCE_API}/klines?symbol=${binanceSymbol}&interval=${interval}&limit=${limit}`
  
  try {
    const response = await fetch(url)
    
    if (!response.ok) {
      throw new Error(`Binance API error: ${response.status}`)
    }
    
    const data = await response.json()
    
    // Binance kline format: [openTime, open, high, low, close, volume, closeTime, ...]
    return data.map((kline: any[]) => ({
      time: Math.floor(kline[0] / 1000), // Convert ms to seconds
      open: parseFloat(kline[1]),
      high: parseFloat(kline[2]),
      low: parseFloat(kline[3]),
      close: parseFloat(kline[4]),
      volume: parseFloat(kline[5]),
    }))
  } catch (error) {
    console.error('Failed to fetch Binance data:', error)
    throw error
  }
}

// Fetch current price from Binance
export async function fetchBinancePrice(symbol: string): Promise<number> {
  const binanceSymbol = toBinanceSymbol(symbol)
  const url = `${BINANCE_API}/ticker/price?symbol=${binanceSymbol}`
  
  try {
    const response = await fetch(url)
    const data = await response.json()
    return parseFloat(data.price)
  } catch (error) {
    console.error('Failed to fetch price:', error)
    throw error
  }
}

// Check if symbol is supported on Binance
export function isBinanceSymbol(symbol: string): boolean {
  const cryptoSymbols = [
    'BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'BNB/USDT', 'XRP/USDT',
    'ADA/USDT', 'DOGE/USDT', 'DOT/USDT', 'AVAX/USDT', 'MATIC/USDT',
    'LINK/USDT', 'UNI/USDT', 'LTC/USDT', 'BCH/USDT', 'ATOM/USDT',
    'ETC/USDT', 'XLM/USDT', 'ALGO/USDT', 'NEAR/USDT', 'FTM/USDT',
    'SHIB/USDT', 'TRX/USDT', 'APT/USDT', 'ARB/USDT', 'OP/USDT',
  ]
  return cryptoSymbols.includes(symbol)
}

// WebSocket for real-time price updates
export function createBinanceWebSocket(
  symbol: string, 
  onPrice: (price: number) => void
): WebSocket | null {
  if (!isBinanceSymbol(symbol)) return null
  
  const binanceSymbol = toBinanceSymbol(symbol).toLowerCase()
  const ws = new WebSocket(`wss://stream.binance.com:9443/ws/${binanceSymbol}@ticker`)
  
  ws.onmessage = (event) => {
    const data = JSON.parse(event.data)
    onPrice(parseFloat(data.c)) // 'c' is the current price
  }
  
  ws.onerror = (error) => {
    console.error('Binance WebSocket error:', error)
  }
  
  return ws
}
